import { Capacitor } from '@capacitor/core';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import type { RecaptchaVerifier } from "firebase/auth";

// IMPORT YOUR NEW MARKETING PAGE HERE
import MarketingLanding from "./MarketingLanding";

function OtpBoxes({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (val: string) => void;
  disabled: boolean;
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const getDigits = (): string[] => {
    const arr: string[] = [];
    for (let i = 0; i < 6; i++) arr.push(value[i] || "");
    return arr;
  };
  const digits = getDigits();

  const focusBox = (i: number) => {
    if (i >= 0 && i < 6) setTimeout(() => inputRefs.current[i]?.focus(), 0);
  };

  const buildValue = (arr: string[]) => arr.join("").replace(/[^0-9]/g, "");

  const handleChange = (i: number, raw: string) => {
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      const arr = getDigits();
      arr[i] = "";
      onChange(buildValue(arr));
      return;
    }
    if (cleaned.length > 1) {
      const full = cleaned.slice(0, 6);
      onChange(full);
      focusBox(Math.min(full.length, 5));
      return;
    }
    const arr = getDigits();
    arr[i] = cleaned[0];
    onChange(buildValue(arr));
    if (i < 5) focusBox(i + 1);
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = getDigits();
      if (arr[i]) {
        arr[i] = "";
        onChange(buildValue(arr));
      } else if (i > 0) {
        arr[i - 1] = "";
        onChange(buildValue(arr));
        focusBox(i - 1);
      }
    } else if (e.key === "ArrowLeft") {
      focusBox(i - 1);
    } else if (e.key === "ArrowRight") {
      focusBox(i + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) {
      onChange(pasted);
      focusBox(Math.min(pasted.length, 5));
    }
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="input-otp">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          value={d}
          disabled={disabled}
          className="w-11 h-12 text-center text-lg font-semibold border-2 border-border rounded-lg bg-background text-foreground focus:border-primary focus:outline-none transition-colors"
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          data-testid={`input-otp-${i}`}
          autoComplete={i === 0 ? "one-time-code" : "off"}
        />
      ))}
    </div>
  );
}

function AuthMethods() {
  const { setupRecaptcha, sendPhoneOTP, verifyPhoneOTP } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);
  const hasAutoSubmitted = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) sessionStorage.setItem("pendingReferralCode", ref.toUpperCase());
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [resendTimer]);

  const getFirebaseErrorMessage = (code: string) => {
    switch (code) {
      case "auth/invalid-phone-number": return "Invalid phone number. Use format: +91XXXXXXXXXX";
      case "auth/too-many-requests": return "Too many attempts. Please try again later.";
      case "auth/invalid-verification-code": return "Invalid OTP. Please try again.";
      case "auth/code-expired": return "OTP expired. Please request a new one.";
      default: return "Something went wrong. Please try again.";
    }
  };

  const clearRecaptcha = useCallback(() => {
    try { recaptchaRef.current?.clear(); } catch (_) { }
    recaptchaRef.current = null;
    const el = document.getElementById("recaptcha-container");
    if (el) el.innerHTML = "";
  }, []);

  const doVerify = useCallback(async (code: string) => {
    if (!confirmationResult || code.length !== 6) return;
    setError("");
    setPhoneLoading(true);
    try {
      await verifyPhoneOTP(confirmationResult, code);
    } catch (err: any) {
      console.error("Verify OTP error:", err);
      setError(getFirebaseErrorMessage(err.code));
      setOtp("");
      hasAutoSubmitted.current = false;
    } finally {
      setPhoneLoading(false);
    }
  }, [confirmationResult, verifyPhoneOTP]);

  const handleSendOTP = useCallback(async () => {
    setError("");
    const fullNumber = phoneNumber.startsWith("+") ? phoneNumber : `+91${phoneNumber}`;
    if (fullNumber.replace(/\D/g, "").length < 12) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setPhoneLoading(true);
    try {
      if (!recaptchaRef.current) {
        clearRecaptcha();
        recaptchaRef.current = setupRecaptcha("recaptcha-container");
      }

      const result = await sendPhoneOTP(fullNumber, recaptchaRef.current);
      setConfirmationResult(result);

      if (result && result.autoVerified) {
        await doVerify("000000");
      } else {
        setOtpSent(true);
        setResendTimer(30);
        hasAutoSubmitted.current = false;
      }
    } catch (err: any) {
      console.error("Send OTP error:", err);
      setError(getFirebaseErrorMessage(err.code));
      clearRecaptcha();
    } finally {
      setPhoneLoading(false);
    }
  }, [phoneNumber, setupRecaptcha, sendPhoneOTP, clearRecaptcha, doVerify]);

  const handleOtpChange = useCallback((val: string) => {
    setOtp(val);
    setError("");
    if (val.length === 6 && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      doVerify(val);
    }
  }, [doVerify]);

  const handleResendOTP = useCallback(async () => {
    setError("");
    setOtp("");
    hasAutoSubmitted.current = false;
    clearRecaptcha();
    setPhoneLoading(true);
    try {
      recaptchaRef.current = setupRecaptcha("recaptcha-container");
      const fullNumber = phoneNumber.startsWith("+") ? phoneNumber : `+91${phoneNumber}`;
      const result = await sendPhoneOTP(fullNumber, recaptchaRef.current);
      setConfirmationResult(result);
      setResendTimer(30);
    } catch (err: any) {
      console.error("Resend OTP error:", err);
      setError(getFirebaseErrorMessage(err.code));
      clearRecaptcha();
    } finally {
      setPhoneLoading(false);
    }
  }, [phoneNumber, setupRecaptcha, sendPhoneOTP, clearRecaptcha]);

  return (
    <div className="space-y-4 w-full">
      <p className="text-center text-base font-medium text-foreground">
        Log in or sign up
      </p>

      {error && (
        <p className="text-sm text-destructive text-center" data-testid="text-auth-error">{error}</p>
      )}

      <div className="space-y-3">
        {!otpSent ? (
          <>
            <div className="flex gap-2">
              <div className="flex items-center gap-1.5 px-3 border border-border rounded-md bg-muted text-sm text-muted-foreground">
                <span className="text-base leading-none">🇮🇳</span>
                <span>+91</span>
              </div>
              <Input
                type="tel"
                placeholder="Enter mobile number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className="flex-1 h-11"
                data-testid="input-phone"
              />
            </div>
            <Button
              className="w-full h-11"
              onClick={handleSendOTP}
              disabled={phoneLoading || phoneNumber.length < 10}
              data-testid="button-send-otp"
            >
              {phoneLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get OTP"}
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <span>OTP sent to +91 {phoneNumber}</span>
              <button
                className="text-primary hover:underline"
                onClick={() => { clearRecaptcha(); setOtpSent(false); setOtp(""); setConfirmationResult(null); setError(""); hasAutoSubmitted.current = false; }}
                data-testid="button-change-number"
              >
                Edit
              </button>
            </div>
            <OtpBoxes value={otp} onChange={handleOtpChange} disabled={phoneLoading} />
            {phoneLoading && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Verifying...</span>
              </div>
            )}
            {!phoneLoading && (
              <div className="text-center text-sm">
                {resendTimer > 0 ? (
                  <p className="text-muted-foreground" data-testid="text-resend-timer">
                    Resend OTP in <span className="font-semibold text-foreground">00:{resendTimer.toString().padStart(2, "0")}</span>
                  </p>
                ) : (
                  <button
                    className="text-primary font-medium hover:underline"
                    onClick={handleResendOTP}
                    data-testid="button-resend-otp"
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <p className="text-xs text-muted-foreground text-center">
        By continuing, you agree to our{" "}
        <a href="https://dailyhisaab.com/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
          Terms of Service
        </a>
        {" & "}
        <a href="https://dailyhisaab.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
          Privacy Policy
        </a>
      </p>

      <div id="recaptcha-container" />
    </div>
  );
}

export default function Landing() {
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone ||
      document.referrer.includes("android-app://") ||
      Capacitor.isNativePlatform();

    setIsPWA(isStandalone);

    if (isStandalone) {
      document.body.classList.add("pwa-mode");
    }
  }, []);

  // IF IT IS THE APP: Show the Login Screen
  if (isPWA) {
    return (
      <div className="min-h-screen flex flex-col bg-primary">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-primary relative flex flex-col items-center justify-center px-6 pt-12 pb-20"
          style={{ minHeight: "40vh" }}
        >
          <div className="scale-125">
            <Logo size="lg" variant="light" />
          </div>
          <p className="text-primary-foreground/80 text-sm mt-3 tracking-wide">
            Simple Billing for Indian Businesses
          </p>
        </motion.div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex-1 bg-background rounded-t-3xl -mt-8 relative z-10 px-6 pt-8 pb-6"
        >
          <div className="w-full max-w-sm mx-auto">
            <AuthMethods />
          </div>
        </motion.div>
      </div>
    );
  }

  // IF IT IS THE WEB BROWSER: Show the dedicated Marketing Landing Page!
  return <MarketingLanding />;
}